import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import dropdownIcon from '../../assets/images/icon-filled-arrow-down-no-stem.svg';
import clearIcon from '../../assets/images/icon-filled-cross-round.svg';
import { truncateString } from '../../util/string';

export type MultiSelectDropdownProps = {
  options: Record<string, string>;
  selectedOptions?: string[];
  placeholder?: string;
  showSelected?: boolean;
  showClearIcon?: boolean;
  reflectParentOptionsState?: boolean;
  onClear?: () => void;
  onChange: (selected: string[]) => void;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  selectedClassName?: string;
  dropdownClassName?: string;
  keyAsLabel?: boolean;
};

const MultiSelectDropdown = ({
  options,
  selectedOptions,
  placeholder = 'Select options',
  onChange,
  onClear,
  showSelected = true,
  showClearIcon = true,
  reflectParentOptionsState = false,
  className,
  buttonClassName,
  labelClassName,
  selectedClassName,
  dropdownClassName,
  keyAsLabel = false,
}: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(selectedOptions);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleCheckboxChange = (option: string) => {
    const updated = selected.includes(option) ? selected.filter((item) => item !== option) : [...selected, option];

    setSelected(updated);
    onChange?.(updated);
  };

  const handleClickOutside = (e) => {
    if (!dropdownRef.current?.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (reflectParentOptionsState || (selectedOptions && !isEmpty(selectedOptions))) {
      setSelected(selectedOptions);
    }
  }, [reflectParentOptionsState, selectedOptions]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={classNames('relative content-center text-left', className)} ref={dropdownRef}>
      <div
        className={classNames(
          'border-gray-300 hover:bg-gray-50 flex h-[32px] w-full flex-row items-center gap-x-2 border px-4 text-sm font-medium',
          buttonClassName
        )}
      >
        <button type="button" className="flex w-full flex-row gap-x-2" onClick={toggleDropdown}>
          <p className={classNames('font-bold', labelClassName)}>{placeholder}</p>{' '}
          {showSelected && !keyAsLabel && (
            <p className={classNames(selectedClassName, 'overflow-hidden truncate lg:max-w-[100px]')}>
              {selected.length === Object.keys(options).length && selected.length !== 0
                ? 'All'
                : truncateString(selected.join(', '), 30)}
              {selected.length === 0 && 'None'}
            </p>
          )}
          {showSelected && keyAsLabel && (
            <p className={classNames(selectedClassName, 'overflow-hidden truncate lg:max-w-[100px]')}>
              {selected.length === Object.keys(options).length && selected.length !== 0
                ? 'All'
                : Object.entries(options)
                    .filter(([, value]) => selected.includes(value))
                    .map(([key]) => key)
                    .join(', ')}
              {selected.length === 0 && 'None'}
            </p>
          )}
          <img src={dropdownIcon} width={20} height={20} alt="dropdown icon" />
        </button>
        {showClearIcon && (
          <img src={clearIcon} className="cursor-pointer" width={20} height={20} alt="clear icon" onClick={onClear} />
        )}
      </div>
      {isOpen && (
        <div
          className={classNames('absolute z-10 w-full rounded-md border bg-white shadow-lg', dropdownClassName)}
          style={{
            borderColor: '#E7E9E9',
          }}
        >
          <ul className="max-h-60 list-none overflow-y-auto">
            {Object.entries(options).map(([key, value]) => (
              <li
                key={key}
                onClick={() => handleCheckboxChange(value)}
                className="cursor-pointer rounded-md p-1 hover:bg-slate-1"
              >
                <label className="flex items-start space-x-2" htmlFor={key}>
                  <p>
                    <input
                      type="checkbox"
                      className="form-checkbox cursor-pointer"
                      checked={selected.includes(value)}
                      onChange={() => handleCheckboxChange(value)}
                    />
                  </p>
                  <p className="cursor-pointer">{keyAsLabel ? key : value}</p>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
